import { useCallback, useEffect, useState } from "react";
import clinicAPI from "../../../services/clinicApi";
import ClientSearchPicker from "../common/ClientSearchPicker";
import s from "../clinic.module.css";
import aa from "../AdminPortal/adminApp.module.css";
import { Button, useToast, Field, Input } from "../../ui";

const STATUS_LABEL = {
  pending: "На рассмотрении",
  approved: "Допущен",
  rejected: "Отклонён",
  cancelled: "Отмена",
};

export default function CompetitionRegistrationsPanel({
  sessionId,
  variant = "default",
}) {
  const isSheet = variant === "sheet";
  const toast = useToast();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [patientId, setPatientId] = useState("");
  const [weight, setWeight] = useState("");
  const [age, setAge] = useState("");
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    if (!sessionId) return;
    setLoading(true);
    try {
      const data = await clinicAPI.getSessionRegistrations(sessionId);
      setRows(data.registrations || []);
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка загрузки списка");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [sessionId, toast]);

  useEffect(() => {
    load();
  }, [load]);

  const addAthlete = async () => {
    if (!patientId) {
      toast.error("Выберите клиента");
      return;
    }
    setAdding(true);
    try {
      await clinicAPI.addSessionRegistration(sessionId, {
        patient_id: patientId,
        status: "approved",
        athlete_weight_kg: weight.trim() || undefined,
        athlete_age: age.trim() || undefined,
      });
      toast.success("Добавлен в список");
      setPatientId("");
      setWeight("");
      setAge("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка");
    } finally {
      setAdding(false);
    }
  };

  const setStatus = async (registrationId, status) => {
    try {
      await clinicAPI.updateSessionRegistration(sessionId, registrationId, {
        status,
      });
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка");
    }
  };

  const remove = async (registrationId) => {
    if (!window.confirm("Убрать из списка?")) return;
    try {
      await clinicAPI.removeSessionRegistration(sessionId, registrationId);
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка");
    }
  };

  const approved = rows.filter((r) => r.status === "approved");
  const pending = rows.filter((r) => r.status === "pending");
  const rejected = rows.filter((r) => r.status === "rejected");

  const statsText = `Допущено: ${approved.length}${
    pending.length > 0 ? ` · заявок: ${pending.length}` : ""
  }${rejected.length > 0 ? ` · отклонено: ${rejected.length}` : ""}`;
  const sheetBadge =
    pending.length > 0 || rejected.length > 0
      ? `${approved.length} доп.${pending.length > 0 ? ` · ${pending.length} заяв.` : ""}${rejected.length > 0 ? ` · ${rejected.length} откл.` : ""}`
      : `${approved.length} допущено`;

  const addForm = (
    <div className={isSheet ? aa.competitionSheetAddCard : s.competitionRegAdd}>
      <div className={s.competitionRegPicker}>
        <ClientSearchPicker
          value={patientId}
          onChange={setPatientId}
          className={isSheet ? aa.competitionSheetPicker : undefined}
        />
      </div>
      <div
        className={
          isSheet ? aa.competitionSheetFields : s.competitionRegFields
        }
      >
        <Field label="Вес, кг">
          <Input
            type="number"
            step="0.1"
            min="1"
            max="300"
            inputMode="decimal"
            value={weight}
            onChange={(e) => setWeight(e.target.value)}
            placeholder="45.5"
          />
        </Field>
        <Field label="Возраст">
          <Input
            type="number"
            min="3"
            max="99"
            inputMode="numeric"
            value={age}
            onChange={(e) => setAge(e.target.value)}
            placeholder="12"
          />
        </Field>
      </div>
      <Button
        type="button"
        block
        size="lg"
        className={
          isSheet ? aa.competitionSheetAddBtn : s.competitionRegAddBtn
        }
        onClick={addAthlete}
        disabled={adding}
      >
        {adding ? "Добавление…" : "Добавить в список"}
      </Button>
    </div>
  );

  const listContent = loading ? (
    <p className={isSheet ? aa.competitionSheetEmpty : s.clientTabMuted}>
      Загрузка…
    </p>
  ) : rows.length === 0 ? (
    <p className={isSheet ? aa.competitionSheetEmpty : s.clientTabMuted}>
      Список пуст. Клиенты могут подать заявку в личном кабинете.
    </p>
  ) : (
    <ul
      className={
        isSheet ? aa.competitionSheetList : s.competitionRegList
      }
    >
      {rows.map((r) => {
        const statusClass = isSheet
          ? `${aa.competitionSheetItemStatus} ${
              r.status === "approved"
                ? aa.competitionSheetItemStatusApproved
                : r.status === "pending"
                  ? aa.competitionSheetItemStatusPending
                  : r.status === "rejected"
                    ? aa.competitionSheetItemStatusRejected
                    : ""
            }`
          : `${s.competitionRegStatus} ${
              r.status === "approved"
                ? s.competitionRegStatusApproved
                : r.status === "pending"
                  ? s.competitionRegStatusPending
                  : ""
            }`;

        return (
          <li
            key={r.id}
            className={
              isSheet ? aa.competitionSheetItem : s.competitionRegItem
            }
          >
            <div
              className={
                isSheet ? aa.competitionSheetItemRow : s.competitionRegMain
              }
            >
              <div
                className={
                  isSheet ? aa.competitionSheetItemMain : undefined
                }
              >
                <strong
                  className={
                    isSheet ? aa.competitionSheetItemName : undefined
                  }
                >
                  {r.athlete?.full_name || "—"}
                </strong>
                {r.athlete_weight_kg || r.athlete_age ? (
                  <span
                    className={
                      isSheet ? aa.competitionSheetItemMeta : s.competitionRegMeta
                    }
                  >
                    {[
                      r.athlete_weight_kg ? `${r.athlete_weight_kg} кг` : null,
                      r.athlete_age ? `${r.athlete_age} лет` : null,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                ) : null}
                {!isSheet ? (
                  <span className={statusClass}>
                    {STATUS_LABEL[r.status] || r.status}
                  </span>
                ) : null}
              </div>
              {isSheet ? (
                <span className={statusClass}>
                  {STATUS_LABEL[r.status] || r.status}
                </span>
              ) : null}
            </div>
            <div
              className={
                isSheet
                  ? aa.competitionSheetItemActions
                  : s.competitionRegActions
              }
            >
              {r.status === "pending" ? (
                <>
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => setStatus(r.id, "approved")}
                  >
                    Допустить
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={() => setStatus(r.id, "rejected")}
                  >
                    Отклонить
                  </Button>
                </>
              ) : null}
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => remove(r.id)}
              >
                Убрать
              </Button>
            </div>
          </li>
        );
      })}
    </ul>
  );

  if (isSheet) {
    return (
      <div className={aa.competitionSheet}>
        <div className={aa.competitionSheetHead}>
          <h4 className={aa.competitionSheetTitle}>Участники</h4>
          <span className={aa.competitionSheetBadge}>{sheetBadge}</span>
        </div>
        {addForm}
        {listContent}
      </div>
    );
  }

  return (
    <div className={s.competitionRegPanel}>
      <h4 className={s.clientTabCardTitle}>Участники</h4>
      <p className={s.clientTabMuted}>{statsText}</p>
      {addForm}
      {listContent}
    </div>
  );
}
